import React, { useState } from 'react';
import styles from './EmailVerificationPanel.module.css';
import { EMAIL_VERIFICATION, IAM_PATH } from '../../../../utils/Config';
import axios from "../../../../utils/api/axios";
// import { DialogEnum, showDialog } from '../../../../utils/dialog/Dialog';
import { InnocodeButton, InnocodeDropdown, InnocodeInput, useDialog } from '@intsoft/innocode-core';

function EmailVerificationPanel({ onNext, onEmailChange }) {
  const [emailValue, setEmailValue] = useState('');
  const [selectedDomain, setSelectedDomain] = useState('naver.com');
  const [customDomain, setCustomDomain] = useState('');
  const [isCustomInput, setIsCustomInput] = useState(false);
  const [authCode, setAuthCode] = useState('');
  const [authCodeSent, setAuthCodeSent] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isEmailCodeSendError, setIsEmailCodeSendError] = useState(false);
  const [codeSendErrorText, setCodeSendErrorText] = useState('');
  const { showDialog } = useDialog();

  // 인증 시도 여부를 추적하는 새로운 상태
  const [isCodeVerifiedAttempted, setIsCodeVerifiedAttempted] = useState(false);
  
  // 인증 코드 발송 중 상태를 나타내는 플래그
  const [isSending, setIsSending] = useState(false);
  
  const fullEmail = isCustomInput ? `${emailValue}@${customDomain}` : `${emailValue}@${selectedDomain}`;

  const domainOptions = [
    { label: 'naver.com', value: 'naver.com' },
    { label: 'gmail.com', value: 'gmail.com' },
    { label: 'daum.net', value: 'daum.net' },
    { label: '직접 입력', value: 'custom' },
  ];

  const handleDomainChange = (domain) => {
    if (domain === 'custom') {
      setIsCustomInput(true);
      setSelectedDomain('');
    } else {
      setIsCustomInput(false);
      setSelectedDomain(domain);
    }
  };

  const handleSendCode = async () => {
    if (!emailValue || (!selectedDomain && !customDomain)) {
      setIsEmailCodeSendError(true);
      setCodeSendErrorText('이메일 주소를 입력해주세요.');
      return;
    }
    try {
      // 1. 이메일 존재 여부 확인
      const existRes = await axios.post(`${IAM_PATH}/exist`, { unifiedProfileId: fullEmail });

      if (existRes.data.data) {
        setIsEmailCodeSendError(true);
        setCodeSendErrorText('이미 가입된 이메일입니다.');
        setIsSending(false);
        return;
      }

      setIsSending(true);
      setIsEmailCodeSendError(false);

      // 2. 이메일이 존재하지 않으면 인증 코드 발송
      const sendRes = await axios.post(`${EMAIL_VERIFICATION}/email/send?email=${fullEmail}`);
      
      if (sendRes.data.code === 200) {
        showDialog({
            iconType: "info",
            type: 'info',
            title: "알림",
            message: "인증 코드가 발송되었습니다.",
        });
        // showDialog(DialogEnum.DEFAULT, "알림", "인증 코드가 발송되었습니다.");
        setAuthCodeSent(true);
      } else {
        setIsEmailCodeSendError(true);
        setCodeSendErrorText('인증 코드 발송에 실패했습니다.');
      }
    } catch (error) {
      console.error("이메일 인증 오류:", error);
      setIsEmailCodeSendError(true);
      setCodeSendErrorText('오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setIsSending(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!authCode) {
      setIsCodeVerifiedAttempted(true);
      return;
    }

    try {
      setIsCodeVerifiedAttempted(true);

      const verifyRes = await axios.post(`${EMAIL_VERIFICATION}/email/verify?email=${fullEmail}&code=${authCode}`);

      if (verifyRes.data.code === 200) {
        setIsEmailVerified(true);
        showDialog({
            iconType: "check",
            type: 'info',
            title: "성공",
            message: "인증되었습니다.",
        });
      } else {
        setIsEmailVerified(false);
        // showDialog(DialogEnum.DEFAULT, "실패", "인증 코드가 올바르지 않습니다.");
        showDialog({
          iconType: "warning",
          type: 'error',
          title: "오류",
          message: "인증 코드가 올바르지 않습니다.",
      });
      }
    } catch (err) {
      console.error("인증 코드 확인 오류:", err);
      setIsEmailVerified(false);
      showDialog({
          iconType: "warning",
          type: 'error',
          title: "오류",
          message: "인증 확인 중 오류가 발생했습니다.",
      });
    }
  };

  const handleNext = () => {
    onEmailChange({ email: fullEmail, isVerified: isEmailVerified });
    onNext();
  };

  const isEmailInputEnabled = emailValue && (selectedDomain || customDomain);

  return (
    <>
      <div className={styles.signup_section}>
        <div className={styles.signup_title_row}>
          <div className={`${styles.signup_title} innocode-typography--title-sm innocode-typography--bold`}>
            이메일 ID 생성
          </div>
        </div>
        <div className={styles.signup_section_inner}>
          <div className={styles.email_input_container}>
            <div className={styles.signup_form_label_row}>
              <div className={styles.signup_form_input_group}>
                <div className={styles.signup_form_label_inner}>
                  <div className={styles.signup_form_label}>이메일</div>
                  <div className={styles.signup_form_required}>*</div>
                </div>
                <div className={styles.signup_input_group}>
                  <InnocodeInput
                    type="text"
                    value={emailValue}
                    placeholder="이메일 입력"
                    onChange={(e) => setEmailValue(e.target.value)}
                    disabled={authCodeSent}
                    className={styles.innocodeInput}
                  />
                  <span className={styles.captionNormal}>@</span>
                  <InnocodeInput
                    value={isCustomInput ? customDomain : selectedDomain}
                    placeholder="도메인 입력"
                    onChange={(e) => setCustomDomain(e.target.value)}
                    disabled={!isCustomInput || authCodeSent}
                    className={styles.innocodeInput}
                  />
                  <InnocodeDropdown
                    options={domainOptions}
                    value={isCustomInput ? 'custom' : selectedDomain}
                    onChange={(value) => handleDomainChange(Array.isArray(value) ? value[0] : value)}
                    disabled={authCodeSent}
                    className={styles.innocodeInput}
                  />
                </div>
              </div>
            </div>
            <div className={styles.send_code_btn_container}>
              <InnocodeButton
                size="lg"
                designType={"primary"}
                theme={!isEmailInputEnabled || isSending ? "assistive" : "solid"}
                onClick={handleSendCode}
                disabled={!isEmailInputEnabled || isSending}
              >
                {isSending ? '전송 중...' : (authCodeSent ? '재전송' : '인증 코드 발송')}
              </InnocodeButton>
              {authCodeSent && (
                <div className={styles.send_code_message + " innocode-typography--body-sm-reading innocode-typography--medium"}>
                  인증코드를 발송했습니다. 인증 메일이 도착하지 않았다면 <br />
                  한번 더 눌러 재전송 해주세요.
                </div>
              )}
              {isEmailCodeSendError && (
                <div className={styles.error_message + " innocode-typography--body-sm-reading innocode-typography--medium"}>{codeSendErrorText}</div>
              )}
            </div>
          </div>
          
          <div className={styles.signup_form_label_row}>
            <div className={styles.signup_form_input_group}>
              <div className={styles.signup_form_label_inner}>
                <div className={styles.signup_form_label}>인증코드</div>
                <div className={styles.signup_form_required}>*</div>
              </div>
              <div className={styles.signup_input_group}>
                <InnocodeInput
                  type="text"
                  value={authCode}
                  placeholder="받으신 코드를 입력해주세요."
                  onChange={(e) => setAuthCode(e.target.value)}
                  disabled={!authCodeSent}
                  className={styles.innocodeInput}
                />
                <InnocodeButton
                  size="lg"
                  designType="line"
                  theme="assistive"
                  className={styles.verify_btn}
                  onClick={handleVerifyCode}
                  disabled={!authCodeSent || isEmailVerified}
                >
                  인증
                </InnocodeButton>
              </div>
              {isCodeVerifiedAttempted && (
                isEmailVerified ? (
                  <div className={styles.success_message}>인증되었습니다.</div>
                ) : (
                  <div className={styles.error_message}>인증 코드가 올바르지 않습니다.</div>
                )
              )}
            </div>
          </div>
        </div>
      </div>
      <div className={styles.submit_btn_container}>
        <InnocodeButton
          size="lg"
          className={styles.submit_btn}
          onClick={handleNext}
          disabled={!isEmailVerified}
        >
          다음
        </InnocodeButton>
      </div>
    </>
  );
}

export default EmailVerificationPanel;
